import {
  useState, useEffect, useCallback, useMemo,
} from 'react';
import axiosClient from '../api/axios.client';

const useConversations = (username) => {
  const [conversations, setConversations] = useState([]);
  const [currentConversationId, setCurrentConversationId] = useState(null);

  // Load conversations from backend and localStorage on mount
  useEffect(() => {
    if (!username) return;

    const loadConversations = async () => {
      try {
        // First try to load from backend
        const response = await axiosClient.get('conversations');
        const backendConversations = response.conversations || [];

        // Always replace localStorage with backend data (this ensures deleted conversations are removed)
        try {
          localStorage.setItem(`conversations_${username}`, JSON.stringify(backendConversations));
        } catch (error) {
          console.warn('Failed to save conversations to localStorage:', error);
        }

        setConversations(backendConversations);

        // Set current conversation to the most recent one if none selected
        if (!currentConversationId && backendConversations.length > 0) {
          setCurrentConversationId(backendConversations[0].id);
        }
      } catch (error) {
        console.error('Error loading conversations from backend:', error);
        // Only fallback to localStorage if backend is completely unavailable
        loadFromLocalStorage();
      }
    };

    const loadFromLocalStorage = () => {
      try {
        const saved = localStorage.getItem(`conversations_${username}`);
        if (saved) {
          const parsedConversations = JSON.parse(saved);
          setConversations(parsedConversations);

          // Set current conversation to the most recent one if none selected
          if (!currentConversationId && parsedConversations.length > 0) {
            setCurrentConversationId(parsedConversations[0].id);
          }
        }
      } catch (error) {
        console.warn('Failed to load conversations from localStorage:', error);
      }
    };

    loadConversations();
  }, [username, currentConversationId]);

  // Enhanced addMessageToConversation with better debugging
  const addMessageToConversation = useCallback(async (conversationId, message) => {
    const newMessage = {
      ...message,
      id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      timestamp: message.timestamp || new Date(),
      type: message.type || 'question',
    };

    // Prepare message for backend - convert JSON objects to strings for question/answer types
    let backendMessage = { ...newMessage };
    if (newMessage.type === 'question' || newMessage.type === 'answer') {
      if (typeof newMessage.content === 'object' && newMessage.content !== null) {
        backendMessage.content = JSON.stringify(newMessage.content, null, 2);
      }
    }

    try {
      // Call backend API to add message
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${conversationId}/messages`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
        },
        body: JSON.stringify({ message: backendMessage }),
      });

      if (!response.ok) {
        console.error('Failed to add message to backend:', response.status);
        // Still update localStorage even if backend fails
      }
    } catch (error) {
      console.error('Error adding message to backend:', error);
      // Still update localStorage even if backend fails
    }

    // Update localStorage and state with the original message (not the backend version)
    setConversations((prevConversations) => {
      const updated = prevConversations.map((conv) => {
        if (conv.id === conversationId) {
          const updatedMessages = [...(conv.messages || []), newMessage];

          return {
            ...conv,
            messages: updatedMessages,
            updatedAt: new Date(),
            name: updatedMessages.length === 1 && message.type === 'question'
              ? (typeof message.content === 'string'
                ? (message.content.length > 30 ? `${message.content.substring(0, 30)}...` : message.content)
                : 'New Conversation')
              : conv.name,
          };
        }
        return conv;
      });

      // Force localStorage update immediately
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }

      return updated;
    });
  }, [username]);

  // Enhanced current messages computation with debugging
  const currentMessages = useMemo(() => {
    if (!currentConversationId) {
      return [];
    }

    const conversation = conversations.find((conv) => conv.id === currentConversationId);
    if (!conversation) {
      return [];
    }

    const messages = (conversation.messages || []).map((msg, index) => ({
      ...msg,
      id: msg.id || `msg_${index}_${Date.now()}`,
      timestamp: msg.timestamp || new Date(),
      type: msg.type || 'question',
    }));

    return messages;
  }, [conversations, currentConversationId]);

  const createNewConversation = useCallback(async () => {
    const clientId = `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    const newConversation = {
      id: clientId,
      clientId,
      name: 'New Conversation',
      messages: [],
      createdAt: new Date(),
      updatedAt: new Date(),
      model: 'llama4',
      settings: { temperature: 0.7, maxTokens: 3000 },
    };

    try {
      // Call backend API to create conversation
      const tkn = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/conversations', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
        },
        body: JSON.stringify({
          clientId: newConversation.clientId,
          name: newConversation.name,
          model: newConversation.model,
          settings: newConversation.settings,
        }),
      });

      if (!response.ok) {
        console.error('Failed to create conversation in backend:', response.status);
        // Still create in localStorage even if backend fails
      } else {
        const backendResponse = await response.json();
        // Update the conversation with backend data if needed
        if (backendResponse.conversation) {
          newConversation.id = backendResponse.conversation.id || newConversation.id;
        }
      }
    } catch (error) {
      console.error('Error creating conversation in backend:', error);
      // Still create in localStorage even if backend fails
    }

    // Create in localStorage and state
    setConversations((prev) => {
      const updated = [newConversation, ...prev];
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });

    setCurrentConversationId(newConversation.id);
    return newConversation;
  }, [username]);

  const selectConversation = useCallback((conversationId) => {
    setCurrentConversationId(conversationId);
  }, []);

  const deleteConversation = useCallback(async (conversationId) => {
    try {
      // Call backend API to delete conversation
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${conversationId}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });

      if (!response.ok) {
        console.error('Failed to delete conversation from backend:', response.status);
        // Still delete from localStorage even if backend fails
      } else {
        // Clear localStorage and reload from backend to ensure consistency
        localStorage.removeItem(`conversations_${username}`);
      }
    } catch (error) {
      console.error('Error deleting conversation from backend:', error);
      // Still delete from localStorage even if backend fails
    }

    // Delete from localStorage and state
    setConversations((prev) => {
      const updated = prev.filter((conv) => conv.id !== conversationId);
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });

    if (currentConversationId === conversationId) {
      setCurrentConversationId(null);
    }
  }, [username, currentConversationId]);

  const clearCurrentConversation = useCallback(async () => {
    if (!currentConversationId) return;

    try {
      // Call backend API to clear conversation messages
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${currentConversationId}/messages`, {
        method: 'DELETE',
        credentials: 'include',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });

      if (!response.ok) {
        console.error('Failed to clear conversation in backend:', response.status);
        // Still update localStorage even if backend fails
      }
    } catch (error) {
      console.error('Error clearing conversation in backend:', error);
      // Still update localStorage even if backend fails
    }

    // Update localStorage and state
    setConversations((prev) => {
      const updated = prev.map((conv) => (conv.id === currentConversationId
        ? { ...conv, messages: [], updatedAt: new Date() }
        : conv));

      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });
  }, [currentConversationId, username]);

  const renameConversation = useCallback(async (conversationId, newName) => {
    try {
      // Call backend API to rename conversation
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${conversationId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(tkn ? { Authorization: `Bearer ${tkn}` } : {}),
        },
        body: JSON.stringify({ name: newName }),
      });

      if (!response.ok) {
        console.error('Failed to rename conversation in backend:', response.status);
        // Still update localStorage even if backend fails
      }
    } catch (error) {
      console.error('Error renaming conversation in backend:', error);
      // Still update localStorage even if backend fails
    }

    // Update localStorage and state
    setConversations((prev) => {
      const updated = prev.map((c) => (c.id === conversationId
        ? { ...c, name: newName, updatedAt: new Date().toISOString() }
        : c));
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });
  }, [username]);

  const toggleFavorite = useCallback(async (conversationId) => {
    try {
      // Call backend API to toggle favorite
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${conversationId}/favorite`, {
        method: 'PATCH',
        credentials: 'include',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });

      if (!response.ok) {
        console.error('Failed to toggle favorite in backend:', response.status);
        // Still update localStorage even if backend fails
      }
    } catch (error) {
      console.error('Error toggling favorite in backend:', error);
      // Still update localStorage even if backend fails
    }

    // Update localStorage and state
    setConversations((prev) => {
      const updated = prev.map((c) => (c.id === conversationId
        ? { ...c, favorite: !c.favorite, updatedAt: new Date().toISOString() }
        : c));
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });
  }, [username]);

  const archiveConversation = useCallback(async (conversationId) => {
    try {
      // Call backend API to toggle archive
      const tkn = localStorage.getItem('tkn');
      const response = await fetch(`/api/v1/conversations/${conversationId}/archive`, {
        method: 'PATCH',
        credentials: 'include',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });

      if (!response.ok) {
        console.error('Failed to toggle archive in backend:', response.status);
        // Still update localStorage even if backend fails
      }
    } catch (error) {
      console.error('Error toggling archive in backend:', error);
      // Still update localStorage even if backend fails
    }

    // Update localStorage and state
    setConversations((prev) => {
      const updated = prev.map((c) => (c.id === conversationId
        ? { ...c, archived: !c.archived, updatedAt: new Date().toISOString() }
        : c));
      try {
        localStorage.setItem(`conversations_${username}`, JSON.stringify(updated));
      } catch (error) {
        console.warn('Failed to save conversations to localStorage:', error);
      }
      return updated;
    });
  }, [username]);

  const refreshConversations = useCallback(async () => {
    try {
      // Clear localStorage first to force fresh load
      localStorage.removeItem(`conversations_${username}`);

      // Load from backend
      const tkn = localStorage.getItem('tkn');
      const response = await fetch('/api/v1/conversations', {
        credentials: 'include',
        headers: tkn ? { Authorization: `Bearer ${tkn}` } : {},
      });

      if (response.ok) {
        const data = await response.json();
        const backendConversations = data.conversations || [];

        // Save to localStorage and set state
        try {
          localStorage.setItem(`conversations_${username}`, JSON.stringify(backendConversations));
        } catch (error) {
          console.warn('Failed to save conversations to localStorage:', error);
        }

        setConversations(backendConversations);

        // Set current conversation to the most recent one if none selected
        if (!currentConversationId && backendConversations.length > 0) {
          setCurrentConversationId(backendConversations[0].id);
        }
      } else {
        console.error('Failed to refresh conversations from backend:', response.status);
      }
    } catch (error) {
      console.error('Error refreshing conversations from backend:', error);
    }
  }, [username, currentConversationId]);

  // Make refreshConversations available globally for testing
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.refreshConversations = refreshConversations;
    }
  }, [refreshConversations]);

  return {
    conversations,
    currentConversationId,
    currentMessages,
    createNewConversation,
    selectConversation,
    deleteConversation,
    clearCurrentConversation,
    addMessageToConversation,
    renameConversation,
    toggleFavorite,
    archiveConversation,
    refreshConversations,
  };
};

export { useConversations };
